import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, List, ListItem, Typography, InputBase, Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridSearchIcon } from '@mui/x-data-grid';
import type { SearchRecord } from './types';

type ReusableSearchProps = {
  value: string;
  results: SearchRecord[];
  onChange: (value: string) => void;
  onSelect: (record: SearchRecord) => void;
  placeholder?: string;
  minSearchChars?: number;
  disabled?: boolean;
  width?: { xs?: number; sm?: number; md?: number; lg?: number } | number | string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  noResultsText?: string;
  renderLabel?: (record: SearchRecord, query: string) => React.ReactNode;
  renderBreadcrumb?: (record: SearchRecord) => React.ReactNode;
  id?: string;
};

export default function ReusableSearch({
  value,
  results,
  onChange,
  onSelect,
  placeholder = 'Search',
  minSearchChars = 3,
  disabled = false,
  width = { xs: 160, sm: 220, md: 260, lg: 300 },
  isOpen: controlledOpen,
  onOpenChange,
  noResultsText = 'No results found',
  renderLabel,
  renderBreadcrumb,
  id = 'reusable-search',
}: ReusableSearchProps) {
  const theme = useTheme();
  const colors = theme.palette;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const shouldShowDropdown = open && value.trim().length >= minSearchChars;
  const hasResults = results.length > 0;

  const visibleResults = useMemo(() => results, [results]);

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
    return parts.map((part, index) => part);
  };

  const defaultRenderLabel = (record: SearchRecord, query: string) => (
    <Typography variant="bodyText">{highlightMatch(record.label, query)}</Typography>
  );

  const defaultRenderBreadcrumb = (record: SearchRecord) => (
    <Typography variant="caption" color="textLight.light" fontWeight={300}>
      {record.breadcrumb}
    </Typography>
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    onChange(nextValue);

    if (nextValue.trim().length >= minSearchChars) {
      setOpen(true);
    } else {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleSelect = (record: SearchRecord) => {
    onSelect(record);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!shouldShowDropdown) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!visibleResults.length) return;
        setActiveIndex((prev) => (prev < visibleResults.length - 1 ? prev + 1 : 0));
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!visibleResults.length) return;
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : visibleResults.length - 1));
        break;

      case 'Enter':
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < visibleResults.length) {
          handleSelect(visibleResults[activeIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    setActiveIndex(-1);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedInsidePopper = !!popperRef.current && popperRef.current.contains(target);

      const clickedInsideAnchor = !!anchorRef.current && anchorRef.current.contains(target);

      if (!clickedInsidePopper && !clickedInsideAnchor) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box id={id} ref={anchorRef} sx={{ position: 'relative' }}>
      <InputBase
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={() => {
          if (value.trim().length >= minSearchChars) {
            setOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        sx={{
          color: 'textLight.light',
          backgroundColor: colors.backgroundLight.globalSearch,
          pl: 5,
          pr: 1,
          height: 40,
          width,
          border: '2px solid transparent',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '& .MuiInputBase-input::placeholder': {
            ...theme.typography.filterText,
            color: colors.backgroundLight.globalSearchPlaceHolder,
            opacity: 1,
          },
          '&:hover': {
            backgroundColor: colors.backgroundLight.globalSearch,
            borderColor: colors.backgroundLight.globalSearchBorder,
            '& + .MuiDataGrid-iconButtonContainer, & ~ svg': {
              color: colors.backgroundLight.icon,
            },
          },
          '&.Mui-focused, &:focus-within': {
            backgroundColor: colors.backgroundLight.dark,
            borderColor: colors.backgroundLight.globalSearchBorder,
            '& + .MuiDataGrid-iconButtonContainer, & ~ svg': {
              color: colors.backgroundLight.icon,
            },
          },
        }}
        disabled={disabled}
      />

      <GridSearchIcon
        sx={{
          position: 'absolute',
          top: '50%',
          left: 8,
          transform: 'translateY(-50%)',
          fontSize: 20,
          color: `${colors.textLight.light}`,
          transition: 'color 0.2s ease',
          pointerEvents: 'none',
          '#reusable-search:hover &': {
            color: colors.backgroundLight.icon,
          },
          '#reusable-search:focus-within &': {
            color: colors.backgroundLight.icon,
          },
        }}
      />

      <Popper
        open={shouldShowDropdown}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        style={{ zIndex: 1500 }}
        ref={popperRef}
      >
        <Paper
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
            backgroundColor: `${colors.backgroundLight.globalSearch} !important`,
            color: `${colors.textLight.light} !important`,
            fontSize: { xs: 12, sm: 13, md: 16 },

            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: `${colors.scrollbar.background} !important`,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: `${colors.scrollbar.thumb} !important`,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: `${colors.scrollbar.thumb} !important`,
            },
          }}
        >
          <List sx={{ padding: 0 }}>
            {hasResults ? (
              visibleResults.map((result, index) => {
                const isActive = index === activeIndex;

                return (
                  <ListItem
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setActiveIndex(index)}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      borderBottom: '0.5px solid rgba(239, 246, 255, 0.5)',
                      width,
                      backgroundColor: isActive
                        ? `${colors.backgroundLight.dark} !important`
                        : `${colors.backgroundLight.globalSearch} !important`,
                      color: `${colors.textLight.light} !important`,
                      '&:hover': {
                        backgroundColor: `${colors.backgroundLight.dark} !important`,
                      },
                    }}
                  >
                    {(renderLabel ?? defaultRenderLabel)(result, value)}
                    {(renderBreadcrumb ?? defaultRenderBreadcrumb)(result)}
                  </ListItem>
                );
              })
            ) : (
              <ListItem
                sx={{
                  width,
                  backgroundColor: `${colors.backgroundLight.globalSearch} !important`,
                  color: `${colors.textLight.light} !important`,
                }}
              >
                <Typography variant="bodyText">{noResultsText}</Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
